import { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, IconButton, TextField, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { CommonDialog, CommonDialogHeader } from '../../Dialog';
import { NavigationAuditRegexService } from '../../../api/services/navigationAuditRegexService';
import { INavigationAuditRegexResponse } from '../../../types/api/response/navigationauditregexresponse';
import { INavigationContent } from '../../../types/store';

interface FlatNav {
  id: number;
  title: string;
  parentTitle: string | null;
  fullPath: string;
}

const flattenNavigations = (navs: INavigationContent[]): FlatNav[] =>
  navs.flatMap((nav) => [
    { id: nav.id, title: nav.title, parentTitle: null, fullPath: nav.title },
    ...(nav.children || []).map((child) => ({
      id: child.id,
      title: child.title,
      parentTitle: nav.title,
      fullPath: `${nav.title} > ${child.title}`,
    })),
  ]);

const isValidRegex = (pattern: string): boolean => {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  editTarget: INavigationAuditRegexResponse | null;
  navigations: INavigationContent[];
  onSaved: (result: INavigationAuditRegexResponse) => void;
}

export const NavigationAuditRegexFormDialog = ({
  isOpen,
  onClose,
  editTarget,
  navigations,
  onSaved,
}: Props) => {
  const flatNavs = flattenNavigations(navigations);

  const [selectedNav, setSelectedNav] = useState<FlatNav | null>(null);
  const [urlRegex, setUrlRegex] = useState('');
  const [description, setDescription] = useState('');
  const [regexError, setRegexError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedParentId, setExpandedParentId] = useState<number | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const keepOpenRef = useRef(false);

  const visibleNavOptions: FlatNav[] = [];
  for (const parent of flatNavs.filter((n) => n.parentTitle === null)) {
    visibleNavOptions.push(parent);
    if (parent.id === expandedParentId) {
      visibleNavOptions.push(...flatNavs.filter((n) => n.parentTitle === parent.title));
    }
  }

  const isEdit = editTarget != null;

  useEffect(() => {
    if (isOpen) {
      if (editTarget) {
        const nav = flatNavs.find((n) => n.id === editTarget.navigation_id) ?? null;
        setSelectedNav(nav);
        setUrlRegex(editTarget.url_regex);
        setDescription(editTarget.description ?? '');
      } else {
        setSelectedNav(null);
        setUrlRegex('');
        setDescription('');
      }
      setRegexError('');
      setSubmitError('');
      setExpandedParentId(null);
      setNavOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editTarget]);

  const onRegexChange = (val: string) => {
    setUrlRegex(val);
    setRegexError(val && !isValidRegex(val) ? 'Invalid regex pattern.' : '');
  };

  const onSubmit = async () => {
    if (!selectedNav) return;
    if (!urlRegex.trim()) {
      setRegexError('URL regex is required.');
      return;
    }
    if (!isValidRegex(urlRegex)) {
      setRegexError('Invalid regex pattern.');
      return;
    }

    setLoading(true);
    setSubmitError('');
    try {
      let result: INavigationAuditRegexResponse;
      if (isEdit) {
        result = await NavigationAuditRegexService.update(editTarget.navigation_id, editTarget.id, {
          url_regex: urlRegex,
          description: description || undefined,
        });
      } else {
        result = await NavigationAuditRegexService.create(selectedNav.id, {
          url_regex: urlRegex,
          description: description || undefined,
        });
      }
      onSaved(result);
      onClose();
    } catch (e: any) {
      setSubmitError(e?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      header={
        <CommonDialogHeader
          title={isEdit ? 'Edit Regex Mapping' : 'Add Regex Mapping'}
          onClose={onClose}
        />
      }
      body={
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Autocomplete
            options={visibleNavOptions}
            getOptionLabel={(o) => o.fullPath}
            value={selectedNav}
            open={navOpen}
            onOpen={() => setNavOpen(true)}
            onClose={() => {
              if (keepOpenRef.current) {
                keepOpenRef.current = false;
                return;
              }
              setNavOpen(false);
            }}
            onChange={(_, val) => {
              if (!val) {
                setSelectedNav(null);
                setExpandedParentId(null);
                return;
              }
              setSelectedNav(val);
              setExpandedParentId(null);
              setNavOpen(false);
            }}
            filterOptions={(x) => x}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={isEdit}
            renderOption={(props, option) => {
              const hasChildren = flatNavs.some((n) => n.parentTitle === option.title);
              const isExpanded = expandedParentId === option.id;
              const isParent = option.parentTitle === null;
              return (
                <li {...props} key={option.id}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    sx={{
                      pl: isParent ? 0 : 3,
                      fontWeight: isParent ? 600 : 400,
                      fontSize: isParent ? 14 : 13,
                      color: isParent ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {option.title}
                    {hasChildren && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          keepOpenRef.current = true;
                          setExpandedParentId((prev) => (prev === option.id ? null : option.id));
                        }}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        ) : (
                          <KeyboardArrowDownIcon
                            fontSize="small"
                            sx={{ color: 'text.secondary' }}
                          />
                        )}
                      </IconButton>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Navigation"
                size="small"
                required
                helperText={isEdit ? 'Navigation cannot be changed after creation.' : ''}
              />
            )}
          />
          <TextField
            label="URL Regex"
            size="small"
            required
            value={urlRegex}
            onChange={(e) => onRegexChange(e.target.value)}
            error={!!regexError}
            helperText={regexError || 'Java-compatible regex to match url_path values.'}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
          <TextField
            label="Description (optional)"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {submitError && (
            <Typography variant="caption" color="error">
              {submitError}
            </Typography>
          )}
        </Box>
      }
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onSubmit}
            disabled={loading || !!regexError || !selectedNav || !urlRegex.trim()}
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </Box>
      }
    />
  );
};
